// Test page for MassiveMusic streaming
// Simple test to isolate playback issues

import React, { useState, useRef } from 'react';
import { Howl } from 'howler';
import { Container, Box, Typography, Button, TextField, Alert, Paper, Divider } from '@mui/material';
import { useAudio } from '@/context/AudioProvider';
import StyledGlobalPlayer from '@/components/StyledGlobalPlayer';

const TestMassiveStream: React.FC = () => {
  const [streamUrl, setStreamUrl] = useState('https://api.staging.lsnto.com/api/massive/stream?trackId=100168841&formatId=33&country=GB');
  const [format, setFormat] = useState<string>('m4a');
  const [status, setStatus] = useState<string>('');
  const [error, setError] = useState<string>('');
  const howlRef = useRef<Howl | null>(null);
  
  // Get AudioProvider context for testing
  const { playTrack, state: audioState } = useAudio();

  const testPlay = async () => {
    setStatus('Testing...');
    setError('');
    
    try {
      // First, test if we can fetch the URL and see the headers
      console.log('🔍 Testing URL:', streamUrl);
      const response = await fetch(streamUrl, {
        method: 'HEAD',
        headers: {
          'Range': 'bytes=0-1023',
        },
      });
      
      console.log('📡 Response status:', response.status);
      // Build headers object manually (Headers.entries() not available in all environments)
      const headersObj: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
      console.log('📡 Response headers:', headersObj);
      console.log('📡 Content-Type:', response.headers.get('content-type'));
      console.log('📡 Accept-Ranges:', response.headers.get('accept-ranges'));
      console.log('📡 Content-Length:', response.headers.get('content-length'));
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      setStatus(`✅ URL accessible. Content-Type: ${contentType || 'not set'}`);
      
      // Now test with Howler.js
      console.log('🎵 Testing with Howler.js, format:', format);
      
      // Stop any existing playback
      if (howlRef.current) {
        howlRef.current.unload();
        howlRef.current = null;
      }
      
      // Parse format - can be comma-separated string or single value
      let formatArray: string[] | string | undefined;
      if (format.includes(',')) {
        formatArray = format.split(',').map(f => f.trim());
      } else if (format.trim() === '') {
        formatArray = undefined; // Auto-detect
      } else {
        formatArray = format.trim();
      }
      
      const howl = new Howl({
        src: [streamUrl],
        format: formatArray,
        html5: true,
        xhr: {
          method: 'GET',
          headers: {
            'Range': 'bytes=0-',
          },
        },
        onload: () => {
          console.log('✅ Howler: Track loaded');
          setStatus('✅ Track loaded successfully');
        },
        onloaderror: (id, error) => {
          console.error('❌ Howler load error:', error, 'ID:', id);
          setError(`Load error: ${error} (ID: ${id})`);
          setStatus('❌ Load failed');
        },
        onplay: () => {
          console.log('▶️ Howler: Playing');
          setStatus('▶️ Playing...');
        },
        onplayerror: (id, error) => {
          console.error('❌ Howler play error:', error, 'ID:', id);
          setError(`Play error: ${error} (ID: ${id})`);
          setStatus('❌ Play failed');
        },
        onend: () => {
          console.log('🏁 Howler: Track ended');
          setStatus('🏁 Track ended');
        },
      });
      
      howlRef.current = howl;
      
      // Try to play
      howl.play();
      
    } catch (err: any) {
      console.error('❌ Test failed:', err);
      setError(err.message || 'Unknown error');
      setStatus('❌ Test failed');
    }
  };

  const testDirectAudio = () => {
    setStatus('Testing with HTML5 audio element...');
    setError('');
    
    const audio = new Audio(streamUrl);
    
    audio.addEventListener('loadstart', () => {
      console.log('📡 HTML5 Audio: loadstart');
      setStatus('📡 Loading...');
    });
    
    audio.addEventListener('loadedmetadata', () => {
      console.log('✅ HTML5 Audio: loadedmetadata');
      console.log('   Duration:', audio.duration);
      console.log('   ReadyState:', audio.readyState);
      setStatus(`✅ Metadata loaded (duration: ${audio.duration}s)`);
    });
    
    audio.addEventListener('canplay', () => {
      console.log('✅ HTML5 Audio: canplay');
      setStatus('✅ Can play');
    });
    
    audio.addEventListener('canplaythrough', () => {
      console.log('✅ HTML5 Audio: canplaythrough');
      setStatus('✅ Can play through');
    });
    
    audio.addEventListener('play', () => {
      console.log('▶️ HTML5 Audio: playing');
      setStatus('▶️ Playing...');
    });
    
    audio.addEventListener('error', (e) => {
      console.error('❌ HTML5 Audio error:', e);
      const error = audio.error;
      if (error) {
        console.error('   Error code:', error.code);
        console.error('   Error message:', error.message);
        setError(`HTML5 Audio error ${error.code}: ${error.message}`);
      } else {
        setError('HTML5 Audio error (unknown)');
      }
      setStatus('❌ Playback failed');
    });
    
    audio.addEventListener('ended', () => {
      console.log('🏁 HTML5 Audio: ended');
      setStatus('🏁 Track ended');
    });
    
    audio.play().catch((err) => {
      console.error('❌ Play promise rejected:', err);
      setError(`Play failed: ${err.message}`);
      setStatus('❌ Play failed');
    });
  };

  const stopPlayback = () => {
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.unload();
      howlRef.current = null;
    }
    setStatus('⏹️ Stopped');
  };

  // Test using AudioProvider's playTrack function
  const testWithAudioProvider = async () => {
    setStatus('Testing with AudioProvider.playTrack...');
    setError('');
    
    try {
      // Create a mock track that matches what the API would return
      const mockTrack = {
        id: 'baa75f0b-360e-41f0-b092-193613f5b6a7', // Jean B track ID
        title: 'U Got the Flow',
        artistName: 'Jean B',
        file_path: undefined, // MassiveMusic tracks have undefined file_path (not null)
        preview_url: null,
        audioUrl: undefined,
        duration: 23,
        album_id: '3adfe32b-ae78-4484-bb8f-9f3c248d6431',
        artist_id: '46c33cef-84af-49bb-8c60-fdbb7e89d780',
      };
      
      console.log('🎵 Testing with AudioProvider.playTrack');
      console.log('   Track:', mockTrack);
      console.log('   This will call getSignedAudioUrl with track.id');
      console.log('   Which should route to /media/audio?filePath=<trackId>');
      console.log('   Which should detect MassiveMusic and return streaming URL');
      
      await playTrack(mockTrack, [mockTrack]);
      setStatus('✅ playTrack called - check console for AudioProvider logs');
    } catch (err: any) {
      console.error('❌ AudioProvider test failed:', err);
      setError(err.message || 'Unknown error');
      setStatus('❌ Test failed');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          MassiveMusic Streaming Test
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Test page to isolate streaming playback issues
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Stream URL"
            value={streamUrl}
            onChange={(e) => setStreamUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Format (e.g., 'm4a', 'aac', 'mp4', 'm4a,aac,mp4', or leave empty for auto)"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            placeholder="m4a"
            helperText="Leave empty for auto-detect, or use comma-separated for fallbacks"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={testPlay}>
            Test with Howler.js (Direct)
          </Button>
          <Button variant="outlined" onClick={testDirectAudio}>
            Test with HTML5 Audio
          </Button>
          <Button variant="contained" color="secondary" onClick={testWithAudioProvider}>
            Test with AudioProvider
          </Button>
          <Button variant="outlined" color="error" onClick={stopPlayback}>
            Stop
          </Button>
        </Box>

        {status && (
          <Alert severity={status.includes('❌') ? 'error' : status.includes('✅') ? 'success' : 'info'} sx={{ mb: 2 }}>
            {status}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Test URLs:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setStreamUrl('https://api.staging.lsnto.com/api/massive/stream?trackId=100168841&formatId=33&country=GB')}
            >
              Jean B - U Got the Flow (staging)
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setStreamUrl('http://localhost:5001/api/massive/stream?trackId=100168841&formatId=33&country=GB')}
            >
              Jean B - U Got the Flow (local)
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Format Options:
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <div>• Empty string: Auto-detect from Content-Type</div>
            <div>• Single: 'm4a', 'aac', 'mp4', 'mp3'</div>
            <div>• Multiple: 'm4a,aac,mp4' (comma-separated fallbacks)</div>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Test with Actual Player Component:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This uses the real StyledGlobalPlayer component. Click "Test with AudioProvider" above first to load a track, then use the player controls below.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
            <StyledGlobalPlayer />
          </Paper>
          {audioState.currentTrack && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current Track: {audioState.currentTrack.title} by {audioState.currentTrack.artistName}
              <br />
              Playing: {audioState.isPlaying ? 'Yes' : 'No'}
              <br />
              Progress: {Math.floor(audioState.progress)}s
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default TestMassiveStream;

